import type { GridApi } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import type { ItemFinancials } from '@/common/api/types/itemFinancials';
import type { DatasetPage } from '@/common/hooks/useDatasetPages';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { financeQueryKeys } from '@/common/queryKeys/finance';
import { ItemFinancialsApi } from '@/features/financials/api/item-financials';
import { buildIdKeyMatcher } from '../hooks/buildIdKeyMatcher';
import { useCrossCachePagesSync } from '../hooks/useCrossCachePagesSync';
import { useTablePagedQuery } from '../hooks/useTablePagedQuery';

type ItemFinancialsFields = Pick<
  ItemFinancials,
  'budgetAmount' | 'capexPercentage' | 'forecastRevenueAmount'
>;

const EMPTY_FINANCIALS: ReadonlyMap<number, ItemFinancialsFields> = new Map();
const matchItemFinancialsKey = buildIdKeyMatcher(financeQueryKeys.itemFinancials.byItemId);

const pickFinancialsFields = (item: ItemFinancials): ItemFinancialsFields => ({
  budgetAmount: item.budgetAmount,
  capexPercentage: item.capexPercentage,
  forecastRevenueAmount: item.forecastRevenueAmount,
});

const itemFinancialsFromSource = (
  _id: number,
  sourceData: ItemFinancials | undefined,
): ItemFinancialsFields | undefined => (sourceData ? pickFinancialsFields(sourceData) : undefined);

type VisibleItemFinancialsContextType = {
  financialsByItemId: ReadonlyMap<number, ItemFinancialsFields>;
};

const VisibleItemFinancialsContext = React.createContext<
  VisibleItemFinancialsContextType | undefined
>(undefined);

const useVisibleItemFinancialsField = <K extends keyof ItemFinancialsFields>(
  itemId: number | undefined,
  field: K,
): NonNullable<ItemFinancialsFields[K]> | undefined => {
  const context = React.useContext(VisibleItemFinancialsContext);
  if (!context || itemId === undefined) return undefined;
  return context.financialsByItemId.get(itemId)?.[field] ?? undefined;
};

export const useVisibleBudget = (itemId: number | undefined): string | undefined =>
  useVisibleItemFinancialsField(itemId, 'budgetAmount');

export const useVisibleCapexPercentage = (itemId: number | undefined): number | undefined =>
  useVisibleItemFinancialsField(itemId, 'capexPercentage');

export const useVisibleForecastRevenue = (itemId: number | undefined): string | undefined =>
  useVisibleItemFinancialsField(itemId, 'forecastRevenueAmount');

export const VisibleItemFinancialsProvider = ({
  pages,
  visiblePageIndices,
  pageIndexById,
  datasetSignature,
  apiRef,
  instanceKey,
  enabled = true,
  children,
}: {
  pages: readonly DatasetPage[];
  visiblePageIndices: readonly number[];
  pageIndexById: ReadonlyMap<number, number>;
  datasetSignature: string;
  apiRef: React.RefObject<GridApi>;
  instanceKey: string;
  enabled?: boolean;
  children: React.ReactNode;
}) => {
  const queryClient = useQueryClient();

  const fetchByIds = React.useCallback(
    async (ids: readonly number[], signal: AbortSignal) => {
      const financials = await ItemFinancialsApi.bulkGetItemFinancials([...ids], signal);
      const financialsById = new Map<number, ItemFinancialsFields>();
      for (const item of financials) {
        financialsById.set(item.itemId, pickFinancialsFields(item));
        queryClient.setQueryData(financeQueryKeys.itemFinancials.byItemId(item.itemId), item);
      }
      return financialsById;
    },
    [queryClient],
  );

  const { loadedById } = useTablePagedQuery<ItemFinancialsFields>({
    providerName: commonQueryKeys.itemsTable.visibleItemFinancials,
    instanceKey,
    pages,
    visiblePageIndices,
    pageIndexById,
    datasetSignature,
    fetchByIds,
    enabled,
    apiRef,
  });

  useCrossCachePagesSync<ItemFinancialsFields, ItemFinancials>({
    providerName: commonQueryKeys.itemsTable.visibleItemFinancials,
    instanceKey,
    pageIndexById,
    datasetSignature,
    matchKey: matchItemFinancialsKey,
    transform: itemFinancialsFromSource,
  });

  const value = React.useMemo<VisibleItemFinancialsContextType>(
    () => ({ financialsByItemId: loadedById.size > 0 ? loadedById : EMPTY_FINANCIALS }),
    [loadedById],
  );

  return (
    <VisibleItemFinancialsContext.Provider value={value}>
      {children}
    </VisibleItemFinancialsContext.Provider>
  );
};
